/**
 * Hook for ApplyRuleBasedTextsSection: search, add new, AI generation.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, useCallback, useMemo } from 'react';
import { useAiTextGeneration } from '@/features/smswiser-campaigns/components/ScaleDropModal/modals/scale-or-edit/creative/useAiTextGeneration';
import { parseSmsTextsInput } from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';
import { MAX_SELECTED_TEXTS, uniqueKeepOrderStrings } from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';

export interface UseApplyRuleBasedTextsParams {
  countryKey: string;
  campaignTexts: string[];
  selectedTexts: string[];
  onTextsChange: (texts: string[]) => void;
  onPerDropBatchesChange: (batches: string[][] | null) => void;
  dropCount?: number;
  isBusy: boolean;
}

export function useApplyRuleBasedTexts(params: UseApplyRuleBasedTextsParams) {
  const {
    countryKey,
    campaignTexts,
    selectedTexts,
    onTextsChange,
    onPerDropBatchesChange,
    dropCount: _dropCount,
    isBusy,
  } = params;

  const [search, setSearch] = useState('');
  const [showAddNew, setShowAddNew] = useState(false);
  const [newTextValue, setNewTextValue] = useState('');
  const [newTextError, setNewTextError] = useState<string | null>(null);
  const [localTexts, setLocalTexts] = useState<string[]>([]);

  const allTexts = useMemo(
    () => uniqueKeepOrderStrings([...campaignTexts, ...localTexts]),
    [campaignTexts, localTexts],
  );

  const filteredTexts = useMemo(() => {
    if (!search.trim()) return allTexts;
    const q = search.toLowerCase();
    return allTexts.filter((t) => t.toLowerCase().includes(q));
  }, [allTexts, search]);

  const addTexts = useCallback(
    (texts: string[]) => {
      const added = uniqueKeepOrderStrings([...localTexts, ...texts]).slice(0, 50);
      setLocalTexts(added);
      setNewTextValue('');
      setNewTextError(null);
    },
    [localTexts],
  );

  const applyNewTextInput = useCallback(() => {
    const { texts, error } = parseSmsTextsInput(newTextValue);
    if (error) {
      setNewTextError(error);
      return;
    }
    if (texts.length) addTexts(texts);
  }, [newTextValue, addTexts]);

  const ai = useAiTextGeneration({
    countryKey,
    onAddTexts: async (texts) => {
      addTexts(texts);
    },
    onSaveGeneratedAndSelectOnly: (texts) => {
      const merged = uniqueKeepOrderStrings([...selectedTexts, ...texts]).slice(0, MAX_SELECTED_TEXTS);
      onTextsChange(merged);
    },
    onPerDropSmsTextsApplied: onPerDropBatchesChange,
    onSaveGeneratedPerDrop: async (batches) => {
      onPerDropBatchesChange(batches);
    },
  });

  return {
    search,
    setSearch,
    showAddNew,
    setShowAddNew,
    newTextValue,
    setNewTextValue,
    newTextError,
    setNewTextError,
    filteredTexts,
    addTexts,
    applyNewTextInput,
    ai,
    isBusy,
  };
}
